// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useMemo } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { documentsApi } from "@/api"
import { comparisonApi } from "@/api"
import type { Document } from "@/api"
import type { ComparisonDetail } from "@/api/comparison"
import { format } from "date-fns"
import DynamicRenderer from "../components/DynamicRenderer"
import { useAuth } from "@umd-dit-ai-workspace/workspaces-component"
import { JobPoller, JobStatus } from "@umd-dit-ai-workspace/jobs-component"
import { configApi } from "@/api/config"
import { PromptsList } from "@/components/PromptsList"
import { extractPromptName, extractAnalysisType } from "@/utils/prompts"
import { ComparisonsTable } from "@/components/ComparisonsTable"
import { FileText, Folder, FolderPlus, AlertTriangle, AlertCircle, X, ChevronDown, ChevronRight, Move, Check } from "lucide-react"
import IndividualAnalysisTray, { type AnalysisResult } from "@/components/IndividualAnalysisTray"
import { exportToAccessiblePDF } from "@/utils/accessiblePdfExport"
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, type DragStartEvent, type DragEndEvent } from '@dnd-kit/core'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { ConfirmDialog } from "@/components/ConfirmDialog"

// Draggable folder component with selection support
function DraggableFolder({ 
  folder, 
  fileCount, 
  isExpanded, 
  onToggle, 
  files,
  isSelected,
  onSelectFolder,
  selectedFileIds,
  onSelectFile
}: { 
  folder: string
  fileCount: number
  isExpanded: boolean
  onToggle: () => void
  files: Document[]
  isSelected: boolean
  onSelectFolder: (e: React.MouseEvent) => void
  selectedFileIds: Set<string>
  onSelectFile: (fileId: string, e: React.MouseEvent) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `folder-${folder}`,
    data: { type: 'folder', folder }
  })

  return (
    <div>
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={`flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 cursor-move transition-all focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          isDragging ? 'opacity-50' : ''
        } ${isSelected ? 'bg-blue-50 border-2 border-blue-600 shadow-sm' : 'border-2 border-transparent'}`}
        onClick={onSelectFolder}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        aria-label={`${isSelected ? 'Selected' : 'Select'} folder ${folder} with ${fileCount} files`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelectFolder(e as any)
          }
        }}
      >
        <div 
          className="flex items-center gap-2 flex-1 cursor-pointer" 
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
        >
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
          <Folder className={`w-5 h-5 ${isSelected ? 'text-blue-700' : 'text-gray-600'}`} />
          <span className={`font-medium uppercase ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{folder}</span>
          <span className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>({fileCount})</span>
          {isSelected && (
            <div className="ml-2 w-5 h-5 bg-blue-700 rounded-full flex items-center justify-center" aria-hidden="true">
              <Check className="w-3.5 h-3.5 text-white stroke-2" />
            </div>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="ml-6 mt-1 space-y-1">
          {files.map(file => (
            <DraggableFile 
              key={file.document_id} 
              file={file} 
              isSelected={selectedFileIds.has(file.document_id)}
              onSelect={(e) => onSelectFile(file.document_id, e)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const DOC_TYPE_BADGE: Record<string, string> = {
  exam:       'bg-red-100 text-red-700',
  syllabus:   'bg-blue-100 text-blue-700',
  assignment: 'bg-purple-100 text-purple-700',
  quiz:       'bg-amber-100 text-amber-700',
  gradedata:  'bg-green-100 text-green-700',
}

// Draggable file component with selection support
function DraggableFile({
  file,
  isSelected,
  onSelect
}: { 
  file: Document
  isSelected: boolean
  onSelect: (e: React.MouseEvent) => void
}) {
  // Check if token estimation is in progress
  const isTokenEstimationPending = file.token_estimation_status === 'processing' || 
    (file.file_size_bytes > 10 * 1024 * 1024 && !file.estimated_token_count)
  const isDisabled = isTokenEstimationPending

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `file-${file.document_id}`,
    data: { type: 'file', fileId: file.document_id, folder: file.folder },
    disabled: isDisabled // Disable dragging when token estimation is pending
  })

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all focus:outline-none focus:ring-1 focus:ring-blue-500 ${
        isDragging ? 'opacity-50' : ''
      } ${
        isDisabled 
          ? 'bg-gray-100 cursor-not-allowed opacity-60 border-2 border-gray-300' 
          : isSelected 
            ? 'bg-blue-50 border-2 border-blue-600 shadow-sm cursor-move hover:bg-gray-100' 
            : 'border-2 border-transparent cursor-move hover:bg-gray-100'
      }`}
      title={isDisabled ? 'Token estimation in progress - please wait' : file.original_filename}
      onClick={(e) => {
        if (isDisabled) return; // Don't allow selection if disabled
        onSelect(e);
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-disabled={isDisabled}
      aria-label={isDisabled ? `Token estimation in progress for ${file.original_filename}` : `${isSelected ? 'Selected' : 'Select'} file ${file.original_filename}`}
      onKeyDown={(e) => {
        if (isDisabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(e as any)
        }
      }}
    >
      {isDisabled ? (
        <svg className="w-4 h-4 flex-shrink-0 text-gray-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        <FileText className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-700' : 'text-gray-500'}`} />
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-sm truncate ${isDisabled ? 'text-gray-600' : isSelected ? 'text-blue-900 font-medium' : 'text-gray-900'}`} title={file.original_filename}>{file.original_filename}</div>
        <div className={`text-xs ${isDisabled ? 'text-gray-500' : isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
          {file.document_type} • {(file.file_size_bytes / 1024 / 1024).toFixed(2)} MB
          {isDisabled ? (
            <> • <span className="italic">Calculating tokens...</span></>
          ) : file.estimated_token_count ? (
            <> • <span className="font-mono">{file.estimated_token_count.toLocaleString()} tokens</span></>
          ) : null}
        </div>
      </div>
      {isSelected && !isDisabled && (
        <div className="w-5 h-5 bg-blue-700 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
          <Check className="w-3.5 h-3.5 text-white stroke-2" />
        </div>
      )}
    </div>
  )
}

// Droppable set area component
function DroppableSetArea({ 
  setId, 
  title, 
  assignedFolders,
  expandedFolders,
  onToggleFolder,
  documents,
  onRemoveFile,
  onRemoveFolder
}: { 
  setId: string
  title: string
  assignedFolders: Map<string, string[]>
  expandedFolders: Set<string>
  onToggleFolder: (folder: string) => void
  documents: Document[]
  onRemoveFile: (folder: string, fileId: string) => void
  onRemoveFolder: (folder: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: setId,
    data: { type: 'set', setId }
  })

  const getFile = (fileId: string) => documents.find(d => d.document_id === fileId)
  
  // Calculate total tokens for this set
  const totalTokens = Array.from(assignedFolders.values())
    .flat()
    .reduce((sum, fileId) => {
      const file = getFile(fileId)
      return sum + (file?.estimated_token_count || 0)
    }, 0)

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 border-2 rounded-lg p-4 transition-all ${
        isOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
      }`}
    >
      <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <div className="text-right">
          <div className="text-sm text-gray-600">
            {Array.from(assignedFolders.values()).flat().length} files
          </div>
          {totalTokens > 0 && (
            <div className="text-xs text-gray-500 font-mono">
              {totalTokens.toLocaleString()} tokens
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {assignedFolders.size === 0 ? (
          <div className="text-center py-12">
  <Folder className="w-12 h-12 mx-auto mb-2 text-gray-500" />
  <p className="text-sm text-gray-600">Drag folders or files here</p>
</div>
        ) : (
          Array.from(assignedFolders.entries()).map(([folder, fileIds]) => {
            const isExpanded = expandedFolders.has(folder)
            return (
              <div key={folder} className="border border-gray-200 rounded-lg p-2">
                <div 
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded"
                  onClick={() => onToggleFolder(folder)}
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
                    <Folder className="w-4 h-4 text-gray-600" />
                    <span className="font-medium text-gray-900 uppercase">{folder}</span>
                    <span className="text-xs text-gray-500">({fileIds.length})</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemoveFolder(folder); }}
                    className="text-gray-400 hover:text-red-600 p-1 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                    aria-label={`Remove folder ${folder} from ${title}`}
                    type="button"
                  >
                    <X className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
                
                {isExpanded && (
                  <div className="ml-6 mt-2 space-y-1">
                    {fileIds.map(fileId => {
                      const file = getFile(fileId)
                      if (!file) return null
                      return (
                        <div key={fileId} className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 rounded" title={file.original_filename}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <FileText className="w-3 h-3 text-gray-500 flex-shrink-0" />
                              <span className="text-xs text-gray-700 truncate" title={file.original_filename}>{file.original_filename}</span>
                            </div>
                            <div className="text-xs text-gray-500 ml-5">
                              {file.estimated_token_count && (
                                <span className="font-mono">{file.estimated_token_count.toLocaleString()} tokens</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => onRemoveFile(folder, fileId)}
                            className="text-gray-400 hover:text-red-600 p-1 flex-shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                            aria-label={`Remove ${file.original_filename} from ${title}`}
                            type="button"
                          >
                            <X className="w-3 h-3" aria-hidden="true" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default function ComparisonPage() {
  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create')
  
  // New drag-and-drop state management
  const [unassignedFolders, setUnassignedFolders] = useState<Map<string, string[]>>(new Map())
  const [setAFolders, setSetAFolders] = useState<Map<string, string[]>>(new Map())
  const [setBFolders, setSetBFolders] = useState<Map<string, string[]>>(new Map())
  const [unassignedExpanded, setUnassignedExpanded] = useState<Set<string>>(new Set())
  const [setAExpanded, setSetAExpanded] = useState<Set<string>>(new Set())
  const [setBExpanded, setSetBExpanded] = useState<Set<string>>(new Set())
  const [activeId, setActiveId] = useState<string | null>(null)
  
  // Multi-select state management
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set())
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set())
  const [showMoveModal, setShowMoveModal] = useState(false)
  
  const [comparisonType, setComparisonType] = useState<string>('mixed_comparison')
  const [comparisonResult, setComparisonResult] = useState<any>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [promptType, setPromptType] = useState<'default' | 'workspace'>('default')
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [selectedComparisonDetail, setSelectedComparisonDetail] = useState<ComparisonDetail | null>(null)
  const [viewingComparisonId, setViewingComparisonId] = useState<string | null>(null)
  const [downloadingPdfFor, setDownloadingPdfFor] = useState<string | null>(null)
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [comparisonToDelete, setComparisonToDelete] = useState<string | null>(null)
  
  // Notification banner state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null)

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])
  
  // Token warning and individual analysis mode states
  const [showTokenWarning, setShowTokenWarning] = useState(false)
  const [individualAnalysisMode, setIndividualAnalysisMode] = useState(false)
  const [setAAnalyses, setSetAAnalyses] = useState<Map<string, AnalysisResult>>(new Map())
  const [setBAnalyses, setSetBAnalyses] = useState<Map<string, AnalysisResult>>(new Map())
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisJobIds, setAnalysisJobIds] = useState<Map<string, string>>(new Map())

  const queryClient = useQueryClient()

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  // Get authentication context for org_id and ws_id
  const { selectedOrganizationId, selectedWorkspaceId, userId, user, isAdmin } = useAuth()

  // Query for workspace configuration to get the default LLM model
  const { data: workspaceConfig, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: async () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      console.log('Fetching workspace config for:', { selectedOrganizationId, selectedWorkspaceId })
      const config = await configApi.getWorkspaceConfig(selectedOrganizationId, selectedWorkspaceId)
      console.log('Workspace config loaded:', config)
      return config
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Log config loading status
  if (configError) {
    console.error('Error loading workspace config:', configError)
  }

  // Query for documents list - only run if we have the required parameters
  const { data: documents = [], isLoading: documentsLoading } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return documentsApi.getDocuments(selectedOrganizationId, selectedWorkspaceId)
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: 5000,
  })

  // Helper function to get selected document IDs - defined early to avoid initialization errors
  const getSelectedDocumentIds = () => {
    const setAIds = Array.from(setAFolders.values()).flat()
    const setBIds = Array.from(setBFolders.values()).flat()
    return { setAIds, setBIds, allIds: [...setAIds, ...setBIds] }
  }

  // Calculate total number of selected documents
  const totalSelectedDocs = useMemo(() => {
    const setAIds = Array.from(setAFolders.values()).flat()
    const setBIds = Array.from(setBFolders.values()).flat()
    return setAIds.length + setBIds.length
  }, [setAFolders, setBFolders])

  // Determine if this is a bulk comparison (more than 2 documents)
  const isBulkComparison = totalSelectedDocs > 2

  // Check if any selected documents are grade data type
  const hasGradeDataDocument = useMemo(() => {
    const { setAIds, setBIds } = getSelectedDocumentIds()
    const allSelectedIds = [...setAIds, ...setBIds]
    return allSelectedIds.some(docId => {
      const doc = documents.find(d => d.document_id === docId)
      return doc?.document_type === 'gradedata'
    })
  }, [setAFolders, setBFolders, documents])

  // Check if any selected documents are "other" type
  const hasOtherDocument = useMemo(() => {
    const { setAIds, setBIds } = getSelectedDocumentIds()
    const allSelectedIds = [...setAIds, ...setBIds]
    return allSelectedIds.some(docId => {
      const doc = documents.find(d => d.document_id === docId)
      return doc?.document_type === 'other'
    })
  }, [setAFolders, setBFolders, documents])

  // Prompt selection logic:
  // 1. If "other" type document detected AND other prompt configured → use other prompt
  // 2. If grade data document detected AND grade data prompt configured → use grade data prompt
  // 3. Otherwise for members (non-admins) → use member defaults (bulk or regular comparison)
  // 4. Otherwise for admins → use manually selected prompt
  const effectivePromptType = !isAdmin && workspaceConfig
    ? (hasOtherDocument && workspaceConfig.member_other_doc_prompt_name
        ? workspaceConfig.member_other_doc_prompt_type
        : hasGradeDataDocument && workspaceConfig.member_grade_data_comparison_prompt_name
          ? workspaceConfig.member_grade_data_comparison_prompt_type
          : isBulkComparison
            ? workspaceConfig.member_bulk_comparison_prompt_type
            : workspaceConfig.member_comparison_prompt_type) || promptType
    : promptType

  const effectivePromptName = !isAdmin && workspaceConfig
    ? (hasOtherDocument && workspaceConfig.member_other_doc_prompt_name
        ? workspaceConfig.member_other_doc_prompt_name
        : hasGradeDataDocument && workspaceConfig.member_grade_data_comparison_prompt_name
          ? workspaceConfig.member_grade_data_comparison_prompt_name
          : isBulkComparison
            ? workspaceConfig.member_bulk_comparison_prompt_name
            : workspaceConfig.member_comparison_prompt_name) || null
    : selectedPrompt?.name || null

  const effectivePromptAnalysisType = !isAdmin && workspaceConfig
    ? (hasOtherDocument && workspaceConfig.member_other_doc_prompt_analysis_type
        ? workspaceConfig.member_other_doc_prompt_analysis_type
        : hasGradeDataDocument && workspaceConfig.member_grade_data_comparison_prompt_analysis_type
          ? workspaceConfig.member_grade_data_comparison_prompt_analysis_type
          : isBulkComparison
            ? workspaceConfig.member_bulk_comparison_prompt_analysis_type
            : workspaceConfig.member_comparison_prompt_analysis_type) || selectedPrompt?.analysisType
    : selectedPrompt?.analysisType


  // Check if member has required defaults configured
  const memberHasRequiredDefaults = isAdmin
    ? true // Admins always have access
    : workspaceConfig
      ? hasOtherDocument
        ? Boolean(workspaceConfig.member_other_doc_prompt_name)
        : hasGradeDataDocument
          ? Boolean(workspaceConfig.member_grade_data_comparison_prompt_name)
          : isBulkComparison
            ? Boolean(workspaceConfig.member_bulk_comparison_prompt_name)
            : Boolean(workspaceConfig.member_comparison_prompt_name)
      : false // Members need config to be loaded and configured

  // Clear selected prompt when switching between bulk and non-bulk modes
  useEffect(() => {
    setSelectedPrompt(null)
  }, [isBulkComparison])

  // Query for available prompts based on selected prompt type
  // Filter to show 'other/' prompts when an "other" doc is selected, else comparator/bulk_comparator
  const { data: availablePrompts = [], isLoading: promptsLoading } = useQuery<string[]>({
    queryKey: ['prompts', promptType, selectedOrganizationId, selectedWorkspaceId, 'comparator-filter', isBulkComparison, hasOtherDocument],
    queryFn: async () => {
      let allPrompts: string[] = []
      if (promptType === 'default') {
        allPrompts = await configApi.getPrompts('default')
      } else {
        if (!selectedOrganizationId || !selectedWorkspaceId) {
          throw new Error('Organization and workspace must be selected for workspace prompts')
        }
        allPrompts = await configApi.getPrompts('workspace', selectedOrganizationId, selectedWorkspaceId)
      }
      console.log('All prompts fetched:', allPrompts)

      // Filter based on document type and count
      let filtered: string[]
      if (hasOtherDocument) {
        filtered = allPrompts.filter(prompt => prompt.startsWith('other/'))
        console.log('Filtered other prompts:', filtered)
      } else if (isBulkComparison) {
        // Show bulk comparator prompts for more than 2 documents
        filtered = allPrompts.filter(prompt => prompt.startsWith('bulk comparator/'))
        console.log('Filtered bulk comparator prompts:', filtered)
      } else {
        // Show comparator prompts for 2 documents
        filtered = allPrompts.filter(prompt => prompt.startsWith('comparator/'))
        console.log('Filtered comparator prompts:', filtered)
      }

      return filtered
    },
    enabled: Boolean(
      promptType === 'default' ||
      (promptType === 'workspace' && selectedOrganizationId && selectedWorkspaceId)
    ),
  })

  // Query for comparison history
  const { data: comparisonsData, isLoading: comparisonsLoading, refetch: refetchComparisons } = useQuery({
    queryKey: ['comparisons', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return comparisonApi.listComparisons(selectedOrganizationId, selectedWorkspaceId)
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: activeTab === 'history' ? 10000 : false,
  })

  // Initialize unassigned folders from documents
  useEffect(() => {
    if (documents.length === 0) return
    
    const uploadedDocs = documents.filter(doc => 
      doc.processing_status === 'uploaded' || doc.processing_status === 'completed'
    )
    
    const folderMap = new Map<string, string[]>()
    uploadedDocs.forEach(doc => {
      if (doc.folder) {
        const existing = folderMap.get(doc.folder) || []
        folderMap.set(doc.folder, [...existing, doc.document_id])
      }
    })
    
    // Only update unassigned folders if they haven't been modified
    if (setAFolders.size === 0 && setBFolders.size === 0) {
      setUnassignedFolders(folderMap)
    }
  }, [documents, setAFolders.size, setBFolders.size])

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    const dragData = active.data.current
    const dropData = over.data.current

    if (!dragData || !dropData) return

    const targetSet = dropData.setId as 'set-a' | 'set-b'

    if (dragData.type === 'folder') {
      // Moving entire folder
      const folder = dragData.folder as string
      const fileIds = unassignedFolders.get(folder) || []
      
      if (targetSet === 'set-a') {
        setSetAFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          // Merge and deduplicate
          const merged = [...new Set([...existing, ...fileIds])]
          next.set(folder, merged)
          return next
        })
        setSetAExpanded(prev => new Set(prev).add(folder))
      } else {
        setSetBFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          // Merge and deduplicate
          const merged = [...new Set([...existing, ...fileIds])]
          next.set(folder, merged)
          return next
        })
        setSetBExpanded(prev => new Set(prev).add(folder))
      }
      
      // Remove from unassigned
      setUnassignedFolders(prev => {
        const next = new Map(prev)
        next.delete(folder)
        return next
      })
      
      // Remove folder from selection if it was selected
      setSelectedFolders(prev => {
        const next = new Set(prev)
        next.delete(folder)
        return next
      })
    } else if (dragData.type === 'file') {
      // Moving individual file
      const fileId = dragData.fileId as string
      const folder = dragData.folder as string
      
      if (targetSet === 'set-a') {
        setSetAFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          next.set(folder, [...existing, fileId])
          return next
        })
        setSetAExpanded(prev => new Set(prev).add(folder))
      } else {
        setSetBFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          next.set(folder, [...existing, fileId])
          return next
        })
        setSetBExpanded(prev => new Set(prev).add(folder))
      }
      
      // Remove from unassigned
      setUnassignedFolders(prev => {
        const next = new Map(prev)
        const files = next.get(folder) || []
        const updated = files.filter(id => id !== fileId)
        if (updated.length === 0) {
          next.delete(folder)
        } else {
          next.set(folder, updated)
        }
        return next
      })
      
      // Remove file from selection if it was selected
      setSelectedFiles(prev => {
        const next = new Set(prev)
        next.delete(fileId)
        return next
      })
    }
  }

  // Remove file from set
  const handleRemoveFile = (setId: 'set-a' | 'set-b', folder: string, fileId: string) => {
    const doc = documents.find(d => d.document_id === fileId)
    if (!doc) return

    if (setId === 'set-a') {
      setSetAFolders(prev => {
        const next = new Map(prev)
        const files = next.get(folder) || []
        const updated = files.filter(id => id !== fileId)
        if (updated.length === 0) {
          next.delete(folder)
        } else {
          next.set(folder, updated)
        }
        return next
      })
    } else {
      setSetBFolders(prev => {
        const next = new Map(prev)
        const files = next.get(folder) || []
        const updated = files.filter(id => id !== fileId)
        if (updated.length === 0) {
          next.delete(folder)
        } else {
          next.set(folder, updated)
        }
        return next
      })
    }

    // Add back to unassigned
    setUnassignedFolders(prev => {
      const next = new Map(prev)
      const existing = next.get(folder) || []
      next.set(folder, [...existing, fileId])
      return next
    })
  }

  // Remove entire folder from set
  const handleRemoveFolder = (setId: 'set-a' | 'set-b', folder: string) => {
    let fileIds: string[] = []
    
    if (setId === 'set-a') {
      fileIds = setAFolders.get(folder) || []
      setSetAFolders(prev => {
        const next = new Map(prev)
        next.delete(folder)
        return next
      })
    } else {
      fileIds = setBFolders.get(folder) || []
      setSetBFolders(prev => {
        const next = new Map(prev)
        next.delete(folder)
        return next
      })
    }

    // Add back to unassigned (merge with any existing files)
    setUnassignedFolders(prev => {
      const next = new Map(prev)
      const existing = next.get(folder) || []
      // Merge and deduplicate
      const merged = [...new Set([...existing, ...fileIds])]
      next.set(folder, merged)
      return next
    })
  }

  // Handle folder selection (with Ctrl/Cmd key for multi-select)
  const handleFolderSelect = (folder: string, e: React.MouseEvent) => {
    e.stopPropagation()
    
    const isMultiSelect = e.ctrlKey || e.metaKey
    
    setSelectedFolders(prev => {
      const next = new Set(prev)
      
      if (isMultiSelect) {
        if (next.has(folder)) {
          next.delete(folder)
        } else {
          next.add(folder)
        }
      } else {
        next.clear()
        next.add(folder)
      }
      
      return next
    })
    
    if (!isMultiSelect) {
      setSelectedFiles(new Set())
    }
  }

  // Handle file selection (with Ctrl/Cmd key for multi-select)
  const handleFileSelect = (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    
    const isMultiSelect = e.ctrlKey || e.metaKey
    
    setSelectedFiles(prev => {
      const next = new Set(prev)
      
      if (isMultiSelect) {
        if (next.has(fileId)) {
          next.delete(fileId)
        } else {
          next.add(fileId)
        }
      } else {
        next.clear()
        next.add(fileId)
      }
      
      return next
    })
    
    if (!isMultiSelect) {
      setSelectedFolders(new Set())
    }
  }

  // Handle moving selected items (folders or files) to a set
  const handleMoveSelectedItems = (targetSet: 'set-a' | 'set-b') => {
    // Move selected folders
    selectedFolders.forEach(folder => {
      const fileIds = unassignedFolders.get(folder) || []
      
      if (targetSet === 'set-a') {
        setSetAFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          const merged = [...new Set([...existing, ...fileIds])]
          next.set(folder, merged)
          return next
        })
        setSetAExpanded(prev => new Set(prev).add(folder))
      } else {
        setSetBFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          const merged = [...new Set([...existing, ...fileIds])]
          next.set(folder, merged)
          return next
        })
        setSetBExpanded(prev => new Set(prev).add(folder))
      }
      
      // Remove from unassigned
      setUnassignedFolders(prev => {
        const next = new Map(prev)
        next.delete(folder)
        return next
      })
    })
    
    // Move selected files
    selectedFiles.forEach(fileId => {
      const doc = documents.find(d => d.document_id === fileId)
      if (!doc || !doc.folder) return
      
      const folder = doc.folder
      
      if (targetSet === 'set-a') {
        setSetAFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          next.set(folder, [...existing, fileId])
          return next
        })
        setSetAExpanded(prev => new Set(prev).add(folder))
      } else {
        setSetBFolders(prev => {
          const next = new Map(prev)
          const existing = next.get(folder) || []
          next.set(folder, [...existing, fileId])
          return next
        })
        setSetBExpanded(prev => new Set(prev).add(folder))
      }
      
      // Remove from unassigned
      setUnassignedFolders(prev => {
        const next = new Map(prev)
        const files = next.get(folder) || []
        const updated = files.filter(id => id !== fileId)
        if (updated.length === 0) {
          next.delete(folder)
        } else {
          next.set(folder, updated)
        }
        return next
      })
    })
    
    // Clear selection and close modal
    setSelectedFolders(new Set())
    setSelectedFiles(new Set())
    setShowMoveModal(false)
  }

  // Calculate total selected items
  const totalSelectedItems = selectedFolders.size + selectedFiles.size

  // Clear selection when switching tabs or when comparison result appears
  useEffect(() => {
    setSelectedFolders(new Set())
    setSelectedFiles(new Set())
  }, [activeTab, comparisonResult])

  // Calculate total tokens
  const calculateTotalTokens = (documentIds: string[]) => {
    return documentIds.reduce((total, id) => {
      const doc = documents.find(d => d.document_id === id)
      return total + (doc?.estimated_token_count || 0)
    }, 0)
  }

  // Handle job completion
  const handleJobComplete = async (jobData: any) => {
    console.log(`Job ${currentJobId} completed`, jobData)

    // The job metadata contains the comparison_id - use it to fetch the full results
    const comparison_id = jobData.metadata?.comparison_id
    
    if (!comparison_id) {
      console.error('No comparison_id found in job metadata')
      setError('Comparison completed but comparison ID not found. Please check the history tab.')
      setIsProcessing(false)
      setCurrentJobId(null)
      setJobStatus(null)
      queryClient.invalidateQueries({ queryKey: ['comparisons'] })
      return
    }

    try {
      console.log(`Fetching comparison results for comparison_id: ${comparison_id}`)
      
      // Fetch the full comparison data from the comparison API
      const comparisonDetail = await comparisonApi.getComparison(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        comparison_id,
        true  // include_results = true
      )
      
      console.log('Comparison detail fetched:', comparisonDetail)
      
      // Set the comparison result with the data from the comparison API
      if (comparisonDetail.comparison_data) {
        setComparisonResult(comparisonDetail.comparison_data)
        console.log('✅ Comparison results loaded successfully')
      } else {
        console.warn('Comparison detail fetched but no comparison_data found')
        setError('Comparison completed but results are not yet available. Please check the history tab.')
      }
    } catch (err) {
      console.error('Error fetching comparison results:', err)
      setError('Comparison completed but could not retrieve results. Please check the history tab.')
    } finally {
      setIsProcessing(false)
      setCurrentJobId(null)
      setJobStatus(null)
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['comparisons'] }) // Refresh comparisons list
    }
  }

  // Handle job error
  const handleJobError = (error: Error) => {
    console.error('Comparison job failed:', error)
    setIsProcessing(false)
    setCurrentJobId(null)
    setJobStatus('ERROR')
    setError(error.message || 'Comparison job failed')
  }

  // Real comparison mutation - uses job polling
  const compareMutation = useMutation({
    mutationFn: async ({ 
      documentIds, 
      comparisonType, 
      setA, 
      setB, 
      isBulk,
      unifiedAnalysisMode 
    }: { 
      documentIds: string[], 
      comparisonType: string,
      setA?: string[],
      setB?: string[],
      isBulk: boolean,
      unifiedAnalysisMode?: boolean
    }) => {
      console.log('Starting document comparison for documents:', documentIds)
      setIsProcessing(true)
      setError(null)
      setComparisonResult(null)
      setJobStatus('PENDING')

      try {
        if (!selectedOrganizationId || !selectedWorkspaceId || !userId) {
          throw new Error('Missing required parameters: organization, workspace, or user ID')
        }

        // Extract prompt parameters
        // For unified analysis mode, always use the unified_comparison_prompt
        const promptName = unifiedAnalysisMode 
          ? 'threshold_exceeded_bulk_comparison_prompt'
          : (effectivePromptName || undefined);
        const analysisType = unifiedAnalysisMode
          ? 'bulk comparator'
          : (effectivePromptAnalysisType || undefined);

        const result = await comparisonApi.compareDocuments(
          documentIds,
          comparisonType,
          selectedOrganizationId,
          selectedWorkspaceId,
          userId,
          effectivePromptType,
          promptName,
          analysisType,
          isBulk,
          setA,
          setB,
          unifiedAnalysisMode,
          workspaceConfig?.default_llm_model
        )
        console.log('Comparison job queued:', result)
        return result
      } catch (error) {
        console.error('API call failed:', error)

        // If it's a float serialization error, provide helpful debug info
        if (error instanceof Error && error.message?.includes('Float types are not supported')) {
          console.log('This appears to be a backend serialization issue.')
          console.log('The backend comparison likely succeeded, but the response contains unserialized float values.')
          console.log('Check your backend logs and ensure all float values are converted to Decimal or string before JSON response.')
        }

        throw error
      }
    },
    onSuccess: (data) => {
      // Check if this is a job response or direct results
      if (data.job_id) {
        // Job-based processing
        const jobId = data.job_id
        setCurrentJobId(jobId)
      } else if (data.results) {
        // Direct results (synchronous processing for small comparisons)
        setComparisonResult(data.results)
        setJobStatus('COMPLETED')
        setIsProcessing(false)
        console.log('Synchronous comparison completed immediately')
      } else if (data.comparison_id) {
        // Legacy async processing with comparison_id (backward compatibility)
        console.log('Legacy async processing with comparison_id:', data.comparison_id)
        // You could handle this case if needed, but job-based is preferred
        setError('Legacy comparison format not supported. Please update backend.')
        setIsProcessing(false)
      } else {
        console.error('Invalid response format:', data)
        setError('Invalid response format from server')
        setIsProcessing(false)
      }
    },
    onError: (error) => {
      console.error('Document comparison failed:', error)
      setIsProcessing(false)
      setCurrentJobId(null)
      setJobStatus(null)

      // Handle specific error types
      let errorMessage = 'Comparison failed'

      if (error instanceof Error) {
        errorMessage = error.message || 'Comparison failed'

        if (error.message?.includes('Float types are not supported')) {
          errorMessage = 'The comparison completed successfully but there was a data formatting issue. This is usually a backend serialization problem. The comparison may have worked - check your backend logs.'
        } else if (error.message?.includes('data formatting issue')) {
          errorMessage = error.message + ' Check the browser developer console for more details.'
        }
      }

      setError(errorMessage)
      setIsProcessing(false)
    }
  })

  // Query for folders list (extract unique folders from documents)
  const folders = useMemo(() => {
    const uniqueFolders = new Set<string>()
    documents.forEach(doc => {
      if (doc.folder) {
        uniqueFolders.add(doc.folder)
      }
    })
    return Array.from(uniqueFolders).sort()
  }, [documents])

  // Get folder counts
  const getFolderCount = (folder: string) => {
    return documents.filter(doc => doc.folder === folder).length
  }

  // Only require uploaded documents - no pre-analysis needed for direct content comparison
  const uploadedDocuments = documents.filter(doc => doc.processing_status === 'uploaded' || doc.processing_status === 'completed')

  // No filtering needed - we show all folders in the drag interface

  // Handle compare
  const handleCompare = () => {
    const { setAIds, setBIds, allIds } = getSelectedDocumentIds()
    
    if (setAIds.length === 0 || setBIds.length === 0) {
      setError('Please assign documents to both Set A and Set B')
      return
    }
    
    const isBulkComparison = allIds.length > 2
    
    if (isBulkComparison) {
      compareMutation.mutate({ 
        documentIds: allIds,
        setA: setAIds,
        setB: setBIds,
        comparisonType: 'bulk_comparison',
        isBulk: true
      })
    } else {
      compareMutation.mutate({ 
        documentIds: allIds, 
        comparisonType,
        isBulk: false
      })
    }
  }

  // Token limit constant
  const SAFE_TOKEN_LIMIT = 100000

  // Check token limits and show warning
  useEffect(() => {
    if (individualAnalysisMode) {
      setShowTokenWarning(false)
      return
    }

    const { setAIds, setBIds } = getSelectedDocumentIds()
    
    // Check token limits for ANY comparison (including 2 documents)
    if (setAIds.length > 0 && setBIds.length > 0) {
      const setATokens = calculateTotalTokens(setAIds)
      const setBTokens = calculateTotalTokens(setBIds)
      const combinedTokens = setATokens + setBTokens

      // Show warning if combined tokens exceed limit
      if (combinedTokens > SAFE_TOKEN_LIMIT) {
        setShowTokenWarning(true)
      } else {
        setShowTokenWarning(false)
      }
    } else {
      setShowTokenWarning(false)
    }
  }, [setAFolders, setBFolders, individualAnalysisMode])

  // Start individual analysis for all selected documents
  const startIndividualAnalysis = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId || !userId) return

    setIndividualAnalysisMode(true)
    setIsAnalyzing(true)
    setShowTokenWarning(false)
    setError(null)

    const newSetAAnalyses = new Map<string, AnalysisResult>()
    const newSetBAnalyses = new Map<string, AnalysisResult>()

    const { setAIds, setBIds } = getSelectedDocumentIds()

    setAIds.forEach(docId => {
      const doc = documents.find(d => d.document_id === docId)
      if (doc) {
        if (doc.analysis_data) {
          newSetAAnalyses.set(docId, {
            documentId: docId,
            documentName: doc.original_filename,
            status: 'completed',
            analysisData: {
              ...doc.analysis_data,
              document_type: doc.document_type
            },
            tokenCount: doc.estimated_token_count
          })
          console.log(`✅ Set A: Found existing analysis for ${doc.original_filename}`)
        } else {
          newSetAAnalyses.set(docId, {
            documentId: docId,
            documentName: doc.original_filename,
            status: 'pending'
          })
        }
      }
    })

    setBIds.forEach(docId => {
      const doc = documents.find(d => d.document_id === docId)
      if (doc) {
        if (doc.analysis_data) {
          newSetBAnalyses.set(docId, {
            documentId: docId,
            documentName: doc.original_filename,
            status: 'completed',
            analysisData: {
              ...doc.analysis_data,
              document_type: doc.document_type
            },
            tokenCount: doc.estimated_token_count
          })
          console.log(`✅ Set B: Found existing analysis for ${doc.original_filename}`)
        } else {
          newSetBAnalyses.set(docId, {
            documentId: docId,
            documentName: doc.original_filename,
            status: 'pending'
          })
        }
      }
    })

    setSetAAnalyses(newSetAAnalyses)
    setSetBAnalyses(newSetBAnalyses)

    try {
      for (const docId of setAIds) {
        const doc = documents.find(d => d.document_id === docId)
        if (doc && !doc.analysis_data) {
          await analyzeDocument(docId, newSetAAnalyses, setSetAAnalyses)
        }
      }

      for (const docId of setBIds) {
        const doc = documents.find(d => d.document_id === docId)
        if (doc && !doc.analysis_data) {
          await analyzeDocument(docId, newSetBAnalyses, setSetBAnalyses)
        }
      }

      console.log('✅ All documents analyzed successfully')
    } catch (err) {
      console.error('Error during individual analysis:', err)
      setError('Failed to complete all document analyses. Check individual document statuses.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Analyze a single document
  const analyzeDocument = async (
    docId: string, 
    analysesMap: Map<string, AnalysisResult>,
    setAnalysesMap: React.Dispatch<React.SetStateAction<Map<string, AnalysisResult>>>
  ) => {
    try {
      // Update status to processing
      setAnalysesMap(prev => {
        const next = new Map(prev)
        const current = next.get(docId)
        if (current) {
          next.set(docId, { ...current, status: 'processing' })
        }
        return next
      })

      // Start analysis job with unified analysis prompt
      // The prompt is located at: config/prompts/unified comparison/unified_analysis_prompt.txt
      const username = user?.userInfo?.username || user?.name || userId;
      const result = await documentsApi.analyzeDocument(
        docId,
        selectedOrganizationId!,
        selectedWorkspaceId!,
        userId!,
        username, // Username for display
        'default', // Use default prompt type
        'general_analysis_prompt', // Prompt name (without .txt extension)
        'analyzer', // Analysis type (subdirectory in config/prompts/)
        undefined, // template_type (optional)
        undefined, // template_name (optional)
        workspaceConfig?.default_llm_model // LLM model from workspace config
      )

      const jobId = result.job_id

      // Store job ID for tracking
      setAnalysisJobIds(prev => {
        const next = new Map(prev)
        next.set(docId, jobId)
        return next
      })

      // Poll for job completion
      await pollAnalysisJob(docId, jobId, setAnalysesMap)

    } catch (err) {
      console.error(`Failed to analyze document ${docId}:`, err)
      setAnalysesMap(prev => {
        const next = new Map(prev)
        const current = next.get(docId)
        if (current) {
          next.set(docId, { 
            ...current, 
            status: 'failed',
            error: err instanceof Error ? err.message : 'Analysis failed'
          })
        }
        return next
      })
    }
  }

  // Poll for analysis job completion
  const pollAnalysisJob = async (
    docId: string,
    jobId: string,
    setAnalysesMap: React.Dispatch<React.SetStateAction<Map<string, AnalysisResult>>>
  ): Promise<void> => {
    return new Promise((resolve) => {
      const checkStatus = async () => {
        try {
          // Get document to check if analysis is complete
          const docs = await documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!)
          const doc = docs.find(d => d.document_id === docId)

          if (doc?.analysis_data) {
            // Analysis complete
            setAnalysesMap(prev => {
              const next = new Map(prev)
              const current = next.get(docId)
              if (current) {
                next.set(docId, {
                  ...current,
                  status: 'completed',
                  analysisData: {
                    ...doc.analysis_data,
                    document_type: doc.document_type
                  },
                  tokenCount: doc.estimated_token_count
                })
              }
              return next
            })
            resolve()
          } else {
            // Still processing, check again in 2 seconds
            setTimeout(checkStatus, 2000)
          }
        } catch (err) {
          console.error(`Error polling job ${jobId}:`, err)
          setAnalysesMap(prev => {
            const next = new Map(prev)
            const current = next.get(docId)
            if (current) {
              next.set(docId, {
                ...current,
                status: 'failed',
                error: 'Failed to retrieve analysis results'
              })
            }
            return next
          })
          resolve()
        }
      }

      checkStatus()
    })
  }

  // Compare all analyses
  const compareAllAnalyses = () => {
    // Collect all completed analyses
    const setACompletedAnalyses = Array.from(setAAnalyses.values())
      .filter(a => a.status === 'completed' && a.analysisData)
    const setBCompletedAnalyses = Array.from(setBAnalyses.values())
      .filter(a => a.status === 'completed' && a.analysisData)

    if (setACompletedAnalyses.length === 0 || setBCompletedAnalyses.length === 0) {
      setError('No completed analyses to compare. Please wait for analyses to complete.')
      return
    }

    // Get all document IDs for the comparison request
    const allDocIds = [
      ...setACompletedAnalyses.map(a => a.documentId),
      ...setBCompletedAnalyses.map(a => a.documentId)
    ]

    // Use unified analysis comparison mode
    // The backend will extract analysis_data from each document and compare them
    compareMutation.mutate({
      documentIds: allDocIds,
      setA: setACompletedAnalyses.map(a => a.documentId),
      setB: setBCompletedAnalyses.map(a => a.documentId),
      comparisonType: 'bulk_comparison',
      isBulk: true,
      unifiedAnalysisMode: true  // Flag to indicate we're comparing analyses, not raw content
    })
  }

  // Reset individual analysis mode
  const resetIndividualAnalysisMode = () => {
    setIndividualAnalysisMode(false)
    setSetAAnalyses(new Map())
    setSetBAnalyses(new Map())
    setIsAnalyzing(false)
    setAnalysisJobIds(new Map())
  }

  // Handle starting a new comparison - resets everything
  const handleNewComparison = () => {
    // Clear comparison results
    setComparisonResult(null)
    setSelectedComparisonDetail(null)
    
    // Reset individual analysis mode
    resetIndividualAnalysisMode()
    
    // Clear all sets
    setSetAFolders(new Map())
    setSetBFolders(new Map())
    
    // Move all documents back to unassigned
    const folderMap = new Map<string, string[]>()
    const uploadedDocs = documents.filter(doc => 
      doc.processing_status === 'uploaded' || doc.processing_status === 'completed'
    )
    uploadedDocs.forEach(doc => {
      if (doc.folder) {
        const existing = folderMap.get(doc.folder) || []
        folderMap.set(doc.folder, [...existing, doc.document_id])
      }
    })
    setUnassignedFolders(folderMap)
    
    // Clear any errors
    setError(null)
    setShowTokenWarning(false)
    
    // Clear processing state
    setIsProcessing(false)
    setCurrentJobId(null)
    setJobStatus(null)
  }

  // Handle viewing comparison details
  const handleViewComparisonDetails = async (comparisonId: string) => {
    try {
      setViewingComparisonId(comparisonId)
      const detail = await comparisonApi.getComparison(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        comparisonId,
        true
      )
      setSelectedComparisonDetail(detail)
      setComparisonResult(detail.comparison_data)
      setActiveTab('create') // Switch to create tab to show results
      
      // Scroll to top to show the results
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Error fetching comparison details:', error)
      setError('Failed to fetch comparison details')
    } finally {
      setViewingComparisonId(null)
    }
  }

  // Handle clearing the historical view
  const handleClearHistoricalView = () => {
    setSelectedComparisonDetail(null)
    setComparisonResult(null)
    setError(null)
  }

  // Handle downloading comparison
  const handleDownloadComparison = async (comparisonId: string) => {
    try {
      const downloadInfo = await comparisonApi.getComparisonDownloadUrl(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        comparisonId
      )

      // Fetch as blob so the download attribute works in Safari (Safari ignores
      // the download attribute on cross-origin URLs like pre-signed S3 URLs)
      const response = await fetch(downloadInfo.download_url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = downloadInfo.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading comparison:', error)
      setError('Failed to download comparison')
    }
  }

  // Handle deleting comparison
  const handleDeleteComparison = (comparisonId: string) => {
    setComparisonToDelete(comparisonId)
    setDeleteConfirmOpen(true)
  }
  
  const confirmDeleteComparison = async () => {
    if (!comparisonToDelete) return
    
    try {
      await comparisonApi.deleteComparison(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        comparisonToDelete
      )
      // Refresh the comparisons list
      refetchComparisons()
      setDeleteConfirmOpen(false)
      setComparisonToDelete(null)
      setNotification({
        type: 'success',
        message: 'Comparison deleted successfully'
      })
    } catch (error) {
      console.error('Error deleting comparison:', error)
      setDeleteConfirmOpen(false)
      setComparisonToDelete(null)
      setNotification({
        type: 'error',
        message: 'Failed to delete comparison'
      })
    }
  }

  // Handle downloading comparison as PDF
  const handleDownloadComparisonPDF = async (comparisonId: string) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return

    setDownloadingPdfFor(comparisonId)

    try {

      // Fetch the full comparison with results
      const detail = await comparisonApi.getComparison(
        selectedOrganizationId,
        selectedWorkspaceId,
        comparisonId,
        true // include_results = true
      )

      if (!detail.comparison_data) {
        alert('No comparison data available to export')
        return
      }

      // Create a temporary container for rendering
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.width = '210mm' // A4 width
      document.body.appendChild(tempContainer)

      // Dynamically import React and ReactDOM to render the component
      const { createRoot } = await import('react-dom/client')
      const root = createRoot(tempContainer)

      // Prepare metadata for the header
      const documentNames = detail.comparison_data.document_names || 
                           detail.comparison_data.documents_compared || []
      const method = detail.comparison_data.method || "Document Comparison"
      const cost = detail.comparison_data.llm_cost || 0
      const timestamp = detail.completed_at || detail.comparison_data.comparison_timestamp
      const promptName = detail.comparison_data.prompt_name || detail.prompt_name
      const analysisType = detail.comparison_data.analysis_type || detail.analysis_type
      const promptType = detail.comparison_data.prompt_type || detail.prompt_type

      // Add a CSS class to expand all collapsible sections for PDF
      const style = document.createElement('style')
      style.textContent = `
        .pdf-export-mode .pdf-expand-all {
          display: block !important;
        }
        .pdf-export-mode .recharts-wrapper {
          position: relative !important;
        }
        .pdf-export-mode .recharts-surface {
          overflow: visible !important;
        }
      `
      document.head.appendChild(style)

      // Render the comparison report with header and dynamic content
      await new Promise<void>((resolve) => {
        root.render(
          <div className="p-8 pdf-export-mode">
            {/* Header with metadata */}
            <div className="mb-8 pb-6 border-b-2 border-gray-300">
              <h1 className="text-3xl font-bold mb-2 text-gray-900">Document Comparison Results</h1>
              <p className="text-xl text-gray-600 mb-4">
                Analyzed {documentNames.length} documents: {documentNames.join(', ')}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="font-medium text-gray-700">{method}</span>
                {promptName && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">Prompt Used:</span>
                    <span className="text-gray-900 font-medium">{promptName}</span>
                    {analysisType && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {analysisType}
                      </span>
                    )}
                    {promptType && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                        {promptType}
                      </span>
                    )}
                  </div>
                )}
                {cost > 0 && <span className="text-gray-700">Cost: ${parseFloat(cost).toFixed(4)}</span>}
                {timestamp && (
                  <span className="text-gray-700">{new Date(timestamp).toLocaleString()}</span>
                )}
              </div>
            </div>

            {/* Dynamic content rendering - same as the UI view */}
            <DynamicRenderer data={detail.comparison_data.comparison_data || detail.comparison_data} />
          </div>
        )

        // Wait longer for charts and visualizations to render
        // Charts need time to calculate dimensions and render SVG elements
        setTimeout(() => resolve(), 2000)
      })

      // Additional wait to ensure all SVG charts are fully rendered and painted
      await new Promise(resolve => setTimeout(resolve, 500))

      // Force a layout recalculation to ensure all dimensions are finalized
      if (tempContainer.firstChild) {
        const element = tempContainer.firstChild as HTMLElement
        element.offsetHeight // Trigger reflow
        
        // Ensure all SVG elements have explicit dimensions before PDF generation
        const svgs = element.querySelectorAll('svg')
        svgs.forEach(svg => {
          const rect = svg.getBoundingClientRect()
          if (rect.width && rect.height && !svg.hasAttribute('width')) {
            svg.setAttribute('width', rect.width.toString())
            svg.setAttribute('height', rect.height.toString())
          }
        })
      }

      // Export to PDF using the unified exportToAccessiblePDF function, reusing the already-opened window
      if (tempContainer.firstChild) {
        // Get the rendered HTML
        const contentHTML = tempContainer.innerHTML;

        // Use the unified export function with pre-rendered HTML
        await exportToAccessiblePDF(null, {
          contentHTML: contentHTML,
          title: `Comparison Report - ${documentNames.join(' vs ')}`,
          documentName: `Comparison_${comparisonId.slice(0, 8)}`
        });
      }

      // Cleanup
      root.unmount()
      document.body.removeChild(tempContainer)
      document.head.removeChild(style)
    } catch (error) {
      console.error('PDF download failed:', error)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setDownloadingPdfFor(null)
    }
  }

 // Dynamic result renderer - handles any LLM response structure
const renderComparisonResult = (result: any) => {
  if (!result) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <p className="text-yellow-800">
          No comparison results available. The analysis may have failed or returned no data.
        </p>
      </div>
    );
  }

  const documentNames = result.document_names || result.documents_compared || [];
  const setADocuments = result.set_a_documents;
  const setBDocuments = result.set_b_documents;
  const hasSeparateSets = setADocuments && setBDocuments && Array.isArray(setADocuments) && Array.isArray(setBDocuments);
  
  // Don't provide a default object fallback - comparison_data could be a string (markdown)
  const comparisonData = result.comparison_data;
  const method = result.method || "Unknown method";
  const cost = result.llm_cost || 0;
  const timestamp = result.comparison_timestamp;
  const promptName = result.prompt_name || selectedComparisonDetail?.prompt_name;
  const analysisType = result.analysis_type || selectedComparisonDetail?.analysis_type;
  const promptType = result.prompt_type || selectedComparisonDetail?.prompt_type;
  const isStructured = result.is_structured !== false; // Default to true for backwards compatibility

  return (
    <div className="space-y-8">
      {/* Header with metadata */}
      <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm">
        <h2 className="text-3xl font-bold mb-2 text-gray-900">Document Comparison Results</h2>
        
        {/* Show Set A and Set B separately if available */}
        {hasSeparateSets ? (
          <div className="space-y-2">
            <p className="text-xl text-gray-600">
              Analyzed {documentNames.length} documents in two sets
            </p>
            <div className="text-gray-700 space-y-2">
              {([['SET A', setADocuments], ['SET B', setBDocuments]] as [string, string[]][]).map(([label, docs]) => (
                <div key={label} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <strong>{label}</strong>
                  {docs.map((name, i) => {
                    const dtype = documents.find(d => d.original_filename === name)?.document_type
                    return (
                      <span key={i} className="flex items-center gap-1">
                        {i > 0 && <span className="text-gray-400">·</span>}
                        <span>{name}</span>
                        {dtype && (
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${DOC_TYPE_BADGE[dtype] ?? 'bg-gray-100 text-gray-600'}`}>
                            {dtype}
                          </span>
                        )}
                      </span>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xl text-gray-600">
            <span>Analyzed {documentNames.length} documents:</span>
            {documentNames.map((name: string, i: number) => {
              const dtype = documents.find(d => d.original_filename === name)?.document_type
              return (
                <span key={i} className="flex items-center gap-1 text-base">
                  {i > 0 && <span className="text-gray-400">·</span>}
                  <span>{name}</span>
                  {dtype && (
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${DOC_TYPE_BADGE[dtype] ?? 'bg-gray-100 text-gray-600'}`}>
                      {dtype}
                    </span>
                  )}
                </span>
              )
            })}
          </div>
        )}
        
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <span className="text-gray-700">{method}</span>
          {!isStructured && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-medium">
              Unstructured Response
            </span>
          )}
          {promptName && (
            <div className="flex items-center gap-2">
              <span className="text-gray-700">Prompt Used:</span>
              <span className="text-gray-900 font-medium">{promptName}</span>
              {analysisType && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                  {analysisType}
                </span>
              )}
              {promptType && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                  {promptType}
                </span>
              )}
            </div>
          )}
          {cost > 0 && <span className="text-gray-700">Cost: ${cost.toFixed(4)}</span>}
          {timestamp && (
            <span className="text-gray-700">{new Date(timestamp).toLocaleString()}</span>
          )}
        </div>
      </div>

      {/* Dynamic content rendering */}
        <DynamicRenderer data={comparisonData} />

        {/* Analysis complete info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            <strong>Analysis Complete:</strong> This comparison used direct content analysis with {method}.
            {cost > 0 && <span> Cost: ${cost.toFixed(4)}</span>}
            {timestamp && <span> • Completed: {new Date(timestamp).toLocaleString()}</span>}
          </p>
        </div>

        {/* New Comparison Button */}
        <div className="flex justify-center pt-4">
          <button
            onClick={handleNewComparison}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Comparison
          </button>
        </div>
      </div>
    );
  };

  // Show message if workspace not selected
  if (!selectedOrganizationId || !selectedWorkspaceId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workspace Required</h2>
          <p className="text-gray-600">Please select an organization and workspace to view and compare documents.</p>
        </div>
      </div>
    )
  }


  return (
    <>
    <ConfirmDialog
      isOpen={deleteConfirmOpen}
      onClose={() => {
        setDeleteConfirmOpen(false)
        setComparisonToDelete(null)
      }}
      onConfirm={confirmDeleteComparison}
      title="Delete Comparison"
      message="Are you sure you want to delete this comparison?"
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
    />
    
    {/* Notification Banner */}
    {notification && (
      <div className="fixed top-16 left-0 right-0 z-[60] flex justify-center pt-4 px-4 pointer-events-none">
        <div className={`pointer-events-auto max-w-2xl w-full rounded-lg shadow-lg border ${
          notification.type === 'success' 
            ? 'bg-green-50 border-green-200' 
            : notification.type === 'error'
            ? 'bg-red-50 border-red-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="p-4 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {notification.type === 'success' ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : notification.type === 'error' ? (
                <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                notification.type === 'success'
                  ? 'text-green-800'
                  : notification.type === 'error'
                  ? 'text-red-800'
                  : 'text-blue-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className={`flex-shrink-0 rounded-lg p-1 transition-colors ${
                notification.type === 'success'
                  ? 'hover:bg-green-100 text-green-600'
                  : notification.type === 'error'
                  ? 'hover:bg-red-100 text-red-600'
                  : 'hover:bg-blue-100 text-blue-600'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )}
    
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-screen bg-white">
        {/* Sidebar - Unassigned Folders - Hidden when viewing comparison results or history tab */}
        {!comparisonResult && !selectedComparisonDetail && activeTab === 'create' && (
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col shadow-lg">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold text-gray-900">Folders</h2>
              {totalSelectedItems > 0 && (
                <button
                  onClick={() => setShowMoveModal(true)}
                  className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  aria-label={`Move ${totalSelectedItems} selected ${totalSelectedItems === 1 ? 'item' : 'items'} to Set A or Set B`}
                  type="button"
                >
                  <Move className="w-4 h-4" aria-hidden="true" />
                  <span>Move ({totalSelectedItems})</span>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-600">
              {totalSelectedItems > 0 
                ? `${selectedFolders.size > 0 ? `${selectedFolders.size} folder${selectedFolders.size !== 1 ? 's' : ''}` : ''}${selectedFolders.size > 0 && selectedFiles.size > 0 ? ' and ' : ''}${selectedFiles.size > 0 ? `${selectedFiles.size} file${selectedFiles.size !== 1 ? 's' : ''}` : ''} selected` 
                : 'Drag folders/files or Ctrl+Click to select'}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {unassignedFolders.size === 0 ? (
              <div className="px-3 py-8 text-center">
                <FolderPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">All folders assigned</p>
              </div>
            ) : (
              <div className="space-y-1">
                {Array.from(unassignedFolders.entries()).map(([folder, fileIds]) => {
                  const folderDocs = fileIds.map(id => documents.find(d => d.document_id === id)).filter(Boolean) as Document[]
                  return (
                    <DraggableFolder
                      key={folder}
                      folder={folder}
                      fileCount={fileIds.length}
                      isExpanded={unassignedExpanded.has(folder)}
                      onToggle={() => {
                        setUnassignedExpanded(prev => {
                          const next = new Set(prev)
                          if (next.has(folder)) {
                            next.delete(folder)
                          } else {
                            next.add(folder)
                          }
                          return next
                        })
                      }}
                      files={folderDocs}
                      isSelected={selectedFolders.has(folder)}
                      onSelectFolder={(e) => handleFolderSelect(folder, e)}
                      selectedFileIds={selectedFiles}
                      onSelectFile={handleFileSelect}
                    />
                  )
                })}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-600">
              <div className="flex justify-between mb-1">
                <span>Unassigned Folders:</span>
                <span className="font-semibold">{unassignedFolders.size}</span>
              </div>
              <div className="flex justify-between">
                <span>Unassigned Files:</span>
                <span className="font-semibold">{Array.from(unassignedFolders.values()).flat().length}</span>
              </div>
            </div>
          </div>
        </div>
        )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="w-full px-6 py-8">
          {/* Header */}
          <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm mb-8">
            <h1 className="text-4xl font-bold mb-4 text-gray-900">
              Document Comparison
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              Organize documents into sets and compare their content using AI analysis
            </p>
            
            {/* Instructions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">How to Compare Documents:</h2>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-fit">Method 1:</span>
                  <span><strong>Drag & Drop</strong> - Drag folders or individual files from the left panel directly into Set A or Set B</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-fit">Method 2:</span>
                  <span><strong>Multi-Select</strong> - Hold <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">Ctrl</kbd> (or <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">Cmd</kbd> on Mac) and click multiple files, then use the <strong>Move</strong> button to assign them to a set</span>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-700">
                  <strong>About Tokens & Document Size:</strong> A "token" is roughly a word or piece of text (about 4 characters). Documents are measured in tokens to estimate their size. 
                  When comparing documents (even just 2 documents) where the combined token count exceeds 100,000 tokens, you'll be given the option to use Individual Analysis Mode.
                  This mode analyzes each document separately first, then compares the summaries instead of the full text, ensuring accurate results with large documents.
                </p>
              </div>
            </div>
          </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => {
                  setActiveTab('create')
                  setError(null)
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'create'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Create New Comparison
                {selectedComparisonDetail && activeTab === 'create' && (
                  <span className="ml-2 text-xs text-gray-500">
                    (Viewing: {selectedComparisonDetail.comparison_id.slice(0, 8)}...)
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setActiveTab('history')
                  setError(null)
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
  activeTab === 'history'
    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
}`}
              >
                Comparison History
{comparisonsData && comparisonsData.count > 0 && (
  <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-blue-800 bg-blue-100 rounded-full">
    {comparisonsData.count}
  </span>
)}
              </button>
            </nav>
          </div>
        </div>
        
        {documentsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Loading documents...</p>
            </div>
          </div>
        ) : activeTab === 'history' ? (
          // Comparison History Tab
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Comparison History</h2>
              <ComparisonsTable
                comparisons={comparisonsData?.comparisons || []}
                onViewDetails={handleViewComparisonDetails}
                onDelete={handleDeleteComparison}
                onDownload={handleDownloadComparison}
                onDownloadPDF={handleDownloadComparisonPDF}
                isLoading={comparisonsLoading}
                loadingComparisonId={viewingComparisonId || downloadingPdfFor}
                loadingAction={viewingComparisonId ? 'view' : downloadingPdfFor ? 'pdf' : null}
              />
            </div>
          </div>
        ) : uploadedDocuments.length < 2 ? (
          <div className="bg-white p-12 rounded-xl shadow-lg text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Need More Documents</h2>
            <p className="text-gray-600">You need at least 2 uploaded documents to perform comparisons.</p>
            <p className="text-gray-600 mt-2">Upload more documents in the Documents tab to use this feature.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Job Poller Component */}
            {currentJobId && (
              <JobPoller
                jobId={currentJobId}
                jobContext={{
                  org_id: selectedOrganizationId || undefined,
                  ws_id: selectedWorkspaceId || undefined
                }}
                onStatusChange={setJobStatus}
                onComplete={handleJobComplete}
                onError={handleJobError}
              />
            )}

            {/* Status Section */}
            {(isProcessing || jobStatus) && (
              <div className="mb-8">
                <JobStatus
                  jobId={currentJobId}
                  jobStatus={jobStatus}
                  isProcessing={isProcessing}
                  processDescription="Processing comparison..."
                />
              </div>
            )}

           {/* Set A and Set B - Side by Side */}
            {!selectedComparisonDetail && !individualAnalysisMode && (
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Organize Documents into Sets
                </h3>

                <div className="grid grid-cols-2 gap-6">
                  <DroppableSetArea
                    setId="set-a"
                    title="Set A"
                    assignedFolders={setAFolders}
                    expandedFolders={setAExpanded}
                    onToggleFolder={(folder) => {
                      setSetAExpanded(prev => {
                        const next = new Set(prev)
                        if (next.has(folder)) {
                          next.delete(folder)
                        } else {
                          next.add(folder)
                        }
                        return next
                      })
                    }}
                    documents={documents}
                    onRemoveFile={(folder, fileId) => handleRemoveFile('set-a', folder, fileId)}
                    onRemoveFolder={(folder) => handleRemoveFolder('set-a', folder)}
                  />

                  <DroppableSetArea
                    setId="set-b"
                    title="Set B"
                    assignedFolders={setBFolders}
                    expandedFolders={setBExpanded}
                    onToggleFolder={(folder) => {
                      setSetBExpanded(prev => {
                        const next = new Set(prev)
                        if (next.has(folder)) {
                          next.delete(folder)
                        } else {
                          next.add(folder)
                        }
                        return next
                      })
                    }}
                    documents={documents}
                    onRemoveFile={(folder, fileId) => handleRemoveFile('set-b', folder, fileId)}
                    onRemoveFolder={(folder) => handleRemoveFolder('set-b', folder)}
                  />
                </div>

                {(setAFolders.size > 0 || setBFolders.size > 0) && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Drag folders from the left panel to assign them to Set A or Set B.
                      {Array.from(setAFolders.values()).flat().length === 0 && Array.from(setBFolders.values()).flat().length > 0 && " Assign documents to Set A to compare."}
                      {Array.from(setAFolders.values()).flat().length > 0 && Array.from(setBFolders.values()).flat().length === 0 && " Assign documents to Set B to compare."}
                      {Array.from(setAFolders.values()).flat().length > 0 && Array.from(setBFolders.values()).flat().length > 0 && " Ready for comparison!"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Token Warning Banner - Hidden when viewing historical comparison */}
            {!selectedComparisonDetail && showTokenWarning && !individualAnalysisMode && (
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      Token Limit Warning
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Your combined document selection exceeds the recommended 100,000 token limit for optimal comparison.
                      Large document sets may produce less accurate results when compared directly.
                    </p>
                    
                {/* Token Stats */}
                    {(() => {
                      const { setAIds, setBIds } = getSelectedDocumentIds()
                      const setATokens = calculateTotalTokens(setAIds)
                      const setBTokens = calculateTotalTokens(setBIds)
                      const combinedTokens = setATokens + setBTokens

                      return (
                        <div className="flex flex-wrap items-center gap-3 text-sm mb-6">
                          <span className="text-gray-700">Set A:</span>
                          <span className="px-3 py-1.5 rounded-md bg-blue-50 text-blue-900 font-medium">
                            {setATokens.toLocaleString()} tokens
                          </span>
                          <span className="text-gray-600">+</span>
                          <span className="text-gray-700">Set B:</span>
                          <span className="px-3 py-1.5 rounded-md bg-blue-50 text-blue-900 font-medium">
                            {setBTokens.toLocaleString()} tokens
                          </span>
                          <span className="text-gray-600">=</span>
                          <span className="px-3 py-1.5 rounded-md bg-red-100 text-red-900 font-semibold">
                            Combined: {combinedTokens.toLocaleString()} tokens
                          </span>
                          <span className="text-gray-600 mx-1">|</span>
                          <span className="text-gray-700">Limit: {SAFE_TOKEN_LIMIT.toLocaleString()} tokens</span>
                        </div>
                      )
                    })()}
                    
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-3">Choose how to proceed:</p>
                        <p className="text-sm text-gray-600 italic mb-4">
                          You can either reduce your selection manually to stay within the token limit, or use our intelligent analysis mode that analyzes each document individually first, then compares the summaries instead of the full content.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Manual Selection Option */}
                        <button
                          onClick={() => {
                            setShowTokenWarning(false)
                            setComparisonResult(null)
                            setError(null)
                          }}
                          className="p-4 text-left border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
                        >
                          <div className="font-medium text-gray-900 mb-1">Manual Selection</div>
                          <div className="text-xs text-gray-600">Remove documents to stay within the 100,000 token limit</div>
                        </button>
                        
                        {/* Individual Analysis Option */}
                        <button
                          onClick={startIndividualAnalysis}
                          disabled={isAnalyzing}
                          className="p-4 text-left border-2 border-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="font-medium text-blue-900 mb-1 flex items-center gap-2">
                            {isAnalyzing ? (
                              <>
                                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                                Analyzing...
                              </>
                            ) : (
                              'Individual Analysis Mode'
                            )}
                          </div>
                          <div className="text-xs text-blue-700">
                            {isAnalyzing 
                              ? 'Starting analysis for each document...' 
                              : 'Analyze each document individually, then compare the summaries (Recommended for large documents)'}
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Individual Analysis Trays - Hidden when viewing historical comparison */}
            {!selectedComparisonDetail && individualAnalysisMode && (
              <div className="space-y-6">
                {/* Mode indicator */}
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Individual Analysis Mode
                      </h3>
                      <p className="text-sm text-gray-700">
                        Each document is being analyzed separately. Once complete, you can compare all analyses.
                      </p>
                    </div>
                    {!isAnalyzing && (
                      <button
                        onClick={resetIndividualAnalysisMode}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                      >
                        Exit Mode
                      </button>
                    )}
                  </div>
                </div>

                {/* Analysis Trays */}
                <div className="grid grid-cols-2 gap-6">
                  <IndividualAnalysisTray
                    title="Set A Analyses"
                    analyses={setAAnalyses}
                    isProcessing={isAnalyzing}
                  />
                  <IndividualAnalysisTray
                    title="Set B Analyses"
                    analyses={setBAnalyses}
                    isProcessing={isAnalyzing}
                  />
                </div>

                {/* Compare button */}
                {!isAnalyzing && (
                  <div className="flex justify-center">
                    <button
                      onClick={compareAllAnalyses}
                      disabled={
                        isProcessing ||
                        Array.from(setAAnalyses.values()).filter(a => a.status === 'completed').length === 0 ||
                        Array.from(setBAnalyses.values()).filter(a => a.status === 'completed').length === 0
                      }
                      className="bg-[#e21833] text-white px-8 py-4 rounded-lg hover:bg-[#c41429] disabled:bg-gray-400 disabled:cursor-not-allowed font-medium text-lg shadow-lg transition-colors"
                    >
                      {isProcessing ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                          Comparing Analyses...
                        </div>
                      ) : (
                        'Compare All Analyses'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Prompt Configuration - Only show for admins */}
            {!selectedComparisonDetail && !individualAnalysisMode && !showTokenWarning &&
              isAdmin &&
              Array.from(setAFolders.values()).flat().length >= 1 &&
              Array.from(setBFolders.values()).flat().length >= 1 && (
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Comparison Configuration</h3>

                <div className="space-y-4">
                  {/* Prompt Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prompt Type
                    </label>
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                      <button
                        onClick={() => {
                          setPromptType('default')
                          setSelectedPrompt(null)
                        }}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          promptType === 'default'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Default
                      </button>
                      <button
                        onClick={() => {
                          setPromptType('workspace')
                          setSelectedPrompt(null)
                        }}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          promptType === 'workspace'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Workspace
                      </button>
                    </div>
                  </div>

                  {/* Prompt List */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Prompt
                      {totalSelectedDocs > 0 && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({hasOtherDocument ? 'Other' : isBulkComparison ? 'Bulk Comparator' : 'Comparator'} - {totalSelectedDocs} documents)
                        </span>
                      )}
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <PromptsList
                        level={promptType}
                        selectedItem={selectedPrompt}
                        onSelectItem={(promptPath) => {
                          // Extract prompt name and analysis type
                          const promptName = extractPromptName(promptPath);
                          const analysisType = extractAnalysisType(promptPath);
                          setSelectedPrompt({ name: promptName, analysisType });
                        }}
                        prompts={availablePrompts}
                        isLoading={promptsLoading}
                      />
                    </div>
                    {availablePrompts.length === 0 && !promptsLoading && (
                      <p className="text-xs text-gray-500 mt-2">
                        No {hasOtherDocument ? 'other' : isBulkComparison ? 'bulk comparator' : 'comparator'} prompts available for {promptType} configuration
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

      {/* Selection Summary */}
            {!selectedComparisonDetail && !individualAnalysisMode &&
              (Array.from(setAFolders.values()).flat().length > 0 ||
               Array.from(setBFolders.values()).flat().length > 0) && (
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex justify-between items-center">
                  <div>
                    {(() => {
                      const { setAIds, setBIds, allIds } = getSelectedDocumentIds()
                      const setATokens = calculateTotalTokens(setAIds)
                      const setBTokens = calculateTotalTokens(setBIds)
                      const combinedTokens = setATokens + setBTokens
                      const exceedsLimit = combinedTokens > SAFE_TOKEN_LIMIT
                      
                      return (
                        <>
                          <p className="text-lg font-semibold text-gray-800">
                            Selected: {allIds.length} document(s)
                            {allIds.length > 2 && (
                              <span className="ml-2 text-sm font-normal text-gray-600">(Bulk Comparison)</span>
                            )}
                          </p>
                          <div className="space-y-2">
                            <div className="flex gap-4 text-sm">
                              <span className="text-gray-700">Set A: {setAIds.length} docs ({setATokens.toLocaleString()} tokens)</span>
                              <span className="text-gray-700">•</span>
                              <span className="text-gray-700">Set B: {setBIds.length} docs ({setBTokens.toLocaleString()} tokens)</span>
                            </div>
                            {exceedsLimit && (
                              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                <span className="text-sm text-red-800 font-medium">
                                  Token limit exceeded: {combinedTokens.toLocaleString()} / {SAFE_TOKEN_LIMIT.toLocaleString()} tokens
                                </span>
                                <span className="text-xs text-red-800 ml-2">
                                  Remove documents to proceed
                                </span>
                              </div>
                            )}
                          </div>
                        </>
                      )
                    })()}
                    <p className="text-sm text-gray-700 mt-1">
                      Comparison Type: {comparisonType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                   </div>
                   
                   {/* Warning message for members without configured defaults */}
                   {!isAdmin && !memberHasRequiredDefaults && (
                     <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
                       <div className="flex items-start gap-3">
                         <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                         <div>
                           <h4 className="font-semibold text-yellow-900 mb-1">
                             Configuration Required
                           </h4>
                           <p className="text-sm text-yellow-800">
                             {hasOtherDocument
                               ? 'Other document comparison defaults have not been configured for members. Please contact your workspace administrator to set up the other document prompt in the Config tab under Member Defaults.'
                               : hasGradeDataDocument
                                 ? 'Grade data comparison defaults have not been configured for members. Please contact your workspace administrator to set up the grade data comparison prompt in the Config tab under Member Defaults.'
                                 : isBulkComparison
                                   ? 'Bulk comparison (3+ documents) defaults have not been configured for members. Please contact your workspace administrator to set up the default comparison prompt in the Config tab.'
                                   : 'Comparison (2 documents) defaults have not been configured for members. Please contact your workspace administrator to set up the default comparison prompt in the Config tab.'}
                           </p>
                         </div>
                       </div>
                     </div>
                   )}

                   <div className="flex gap-3">
                    {!comparisonResult && <button
                      onClick={handleCompare}
                      disabled={
                        Array.from(setAFolders.values()).flat().length === 0 ||
                        Array.from(setBFolders.values()).flat().length === 0 ||
                        isProcessing ||
                        !memberHasRequiredDefaults ||
                        (() => {
                          const { setAIds, setBIds } = getSelectedDocumentIds()
                          const combinedTokens = calculateTotalTokens(setAIds) + calculateTotalTokens(setBIds)
                          return combinedTokens > SAFE_TOKEN_LIMIT
                        })()
                      }
                      className="bg-[#e21833] text-white px-6 py-3 rounded-lg hover:bg-[#c41429] disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                      title={!memberHasRequiredDefaults && !isAdmin ? 'Comparison defaults not configured for members' : ''}
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          Compare Documents
                        </>
                      )}
                    </button>}
                    <button
                      onClick={() => {
                        setSetAFolders(new Map())
                        setSetBFolders(new Map())
                        setComparisonResult(null)
                        setError(null)
                        setCurrentJobId(null)
                        setJobStatus(null)
                        // Reset unassigned folders
                        const folderMap = new Map<string, string[]>()
                        const uploadedDocs = documents.filter(doc =>
                          doc.processing_status === 'uploaded' || doc.processing_status === 'completed'
                        )
                        uploadedDocs.forEach(doc => {
                          if (doc.folder) {
                            const existing = folderMap.get(doc.folder) || []
                            folderMap.set(doc.folder, [...existing, doc.document_id])
                          }
                        })
                        setUnassignedFolders(folderMap)
                      }}
                      className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 font-medium"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800">Comparison Failed</h3>
                  <p className="text-red-600 text-sm mb-3">{error}</p>
                    
                    {error.includes('response formatting issue') && (
                      <div className="space-y-4">
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                          <p className="font-medium text-yellow-800 mb-2">Backend Fix Required:</p>
                          <p className="text-yellow-700 mb-2">This error occurs when your backend returns float values that aren't JSON-serializable. The comparison likely succeeded.</p>
                          <details className="mt-2">
                            <summary className="cursor-pointer font-medium text-yellow-800 hover:text-yellow-900">
                              Show Backend Fix Instructions
                            </summary>
                            <div className="mt-2 pl-4 border-l-2 border-yellow-300">
                              <p className="text-yellow-700 mb-2">Apply one of these fixes to your backend:</p>
                              <ol className="list-decimal list-inside text-yellow-700 space-y-1">
                                <li>Convert floats to strings: <code className="bg-yellow-100 px-1 rounded text-xs">result['llm_cost'] = str(result['llm_cost'])</code></li>
                                <li>Use Decimal: <code className="bg-yellow-100 px-1 rounded text-xs">from decimal import Decimal</code></li>
                                <li>Custom JSON encoder: <code className="bg-yellow-100 px-1 rounded text-xs">json.dumps(data, default=str)</code></li>
                              </ol>
                              <p className="text-yellow-600 mt-2 text-xs">
                                See BACKEND_FIX_GUIDE.md in your project root for detailed instructions.
                              </p>
                            </div>
                          </details>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                          <p className="font-medium text-blue-800 mb-2">What This Means:</p>
                          <ul className="list-disc list-inside text-blue-700 space-y-1">
                            <li>Your document comparison logic is working correctly</li>
                            <li>The analysis completed successfully</li>
                            <li>Only the final response formatting needs fixing</li>
                            <li>Check your backend logs to see the actual results</li>
                          </ul>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                          <p className="font-medium text-green-800 mb-2">Next Steps:</p>
                          <ol className="list-decimal list-inside text-green-700 space-y-1">
                            <li>Check your backend logs - the comparison results should be there</li>
                            <li>Apply the backend fix from BACKEND_FIX_GUIDE.md</li>
                            <li>Redeploy your backend</li>
                            <li>Try the comparison again</li>
                          </ol>
                        </div>
                      </div>
                    )}

                    {/* Generic error handling */}
                    {!error.includes('response formatting issue') && error.includes('Float types are not supported') && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                        <p className="font-medium text-yellow-800 mb-2">Backend Serialization Issue:</p>
                        <p className="text-yellow-700">
                          This is a backend JSON serialization problem. Check BACKEND_FIX_GUIDE.md for the complete solution.
                        </p>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Back button when viewing historical comparison */}
            {selectedComparisonDetail && (
              <div className="mb-6">
                <button
                  onClick={handleClearHistoricalView}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Comparisons
                </button>
              </div>
            )}

            {/* Comparison Results */}
            {comparisonResult && (
              <div>
                {renderComparisonResult(comparisonResult)}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      </div>

        {/* Move Modal */}
        {showMoveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Move {totalSelectedItems} item{totalSelectedItems > 1 ? 's' : ''} to:
              </h3>
              
              <div className="space-y-3 mb-6">
                <div className="text-sm text-gray-600 mb-3">
                  {selectedFolders.size > 0 && (
                    <div className="mb-2">
                      Selected folders:
                      <ul className="list-disc list-inside mt-1 ml-2">
                        {Array.from(selectedFolders).map(folder => (
                          <li key={folder} className="text-gray-800 font-medium uppercase">{folder}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedFiles.size > 0 && (
                    <div>
                      Selected files:
                      <ul className="list-disc list-inside mt-1 ml-2">
                        {Array.from(selectedFiles).map(fileId => {
                          const doc = documents.find(d => d.document_id === fileId)
                          return doc ? (
                            <li key={fileId} className="text-gray-800">{doc.original_filename}</li>
                          ) : null
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => handleMoveSelectedItems('set-a')}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Folder className="w-6 h-6 text-blue-600" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">Set A</div>
                      <div className="text-sm text-gray-600">
                        {Array.from(setAFolders.values()).flat().length} files currently
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                
                <button
                  onClick={() => handleMoveSelectedItems('set-b')}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Folder className="w-6 h-6 text-blue-600" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">Set B</div>
                      <div className="text-sm text-gray-600">
                        {Array.from(setBFolders.values()).flat().length} files currently
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowMoveModal(false)
                    setSelectedFolders(new Set())
                    setSelectedFiles(new Set())
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drag Overlay */}
      <DragOverlay>
        {activeId ? (
          <div className="bg-white border-2 border-blue-500 rounded-lg p-3 shadow-lg">
            <div className="flex items-center gap-2">
              {activeId.startsWith('folder-') ? (
                <>
                  <Folder className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-900 uppercase">
                    {activeId.replace('folder-', '')}
                  </span>
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">File</span>
                </>
              )}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
    </>
  )
}